import type { ReactNode } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';

export interface SectionCardProps {
  /** Card title displayed in the header */
  title: string;
  /** Optional content rendered beside the title (e.g., status badges) */
  titleAddon?: ReactNode;
  /** Optional action elements rendered on the right side of the header */
  actions?: ReactNode;
  /** When true, hides the entire header row (title + actions). Useful when the card content is self-describing. */
  hideHeader?: boolean;
  /**
   * When true, the card fills available height and scrolls its body
   * while keeping the header pinned at the top.
   */
  scrollable?: boolean;
  /** Number of unsaved changes — shows "Save (N) changes" button in header when > 0 */
  changeCount?: number;
  /** Callback when save button is clicked */
  onSave?: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Card body content */
  children: ReactNode;
}

/**
 * SectionCard - Shared container for slideout content sections.
 *
 * Provides a consistent card layout with:
 * - White background, border, rounded corners, 24px padding
 * - Header row with title (Inter 18px/500) and optional actions
 * - Bottom border separator below the header
 * - Children rendered below the separator
 *
 * When `scrollable` is true, the card fills its container height,
 * the header stays pinned, and the body scrolls independently.
 *
 * @example
 * <SectionCard title="Contact overview" actions={<button>Edit</button>}>
 *   <FieldList />
 * </SectionCard>
 */
export function SectionCard({
  title,
  titleAddon,
  actions,
  hideHeader = false,
  scrollable = false,
  changeCount = 0,
  onSave,
  isSaving = false,
  children,
}: SectionCardProps) {
  return (
    <div
      className={`bg-primary border border-primary rounded-xl ${
        scrollable ? 'flex flex-col h-full overflow-hidden' : 'p-4 lg:p-6'
      }`}
    >
      {!hideHeader && (
        <div
          className={`flex items-center justify-between pb-3 lg:pb-4 min-h-[32px] lg:min-h-[36px] border-b border-gray-200 dark:border-gray-700 ${
            scrollable ? 'shrink-0 px-4 lg:px-6 pt-3 lg:pt-6' : 'mb-3 lg:mb-4'
          }`}
        >
          <div className='flex items-center gap-2 lg:gap-3'>
            <h2 className='text-sm lg:text-base 2xl:text-heading-3 font-semibold 2xl:font-medium text-primary'>
              {title}
            </h2>
            {titleAddon}
          </div>
          <div className='flex items-center gap-3'>
            {changeCount > 0 && onSave && (
              <div className='hidden lg:block'>
                <Tooltip
                  title='Save changes'
                  description={`${changeCount} unsaved change${changeCount !== 1 ? 's' : ''}`}
                  placement='top'
                >
                  <TooltipTrigger>
                    <Button
                      type='button'
                      color='primary'
                      size='sm'
                      onClick={onSave}
                      isDisabled={isSaving}
                      isLoading={isSaving}
                      // Aligns with the SubmitButton e2e contract
                      // (frontend/src/lib/base/forms/submit-button.tsx:157):
                      // `data-state="saving"` while a save is in flight,
                      // attribute absent otherwise. Lets e2e tests wait for
                      // the round-trip via the shared
                      // `submitButton(...).waitForIdle()` helper rather than
                      // matching the changing button label, which silently
                      // toggles between "Save changes" and "Saving..." and
                      // makes role-based locators stop matching mid-save.
                      data-state={isSaving ? 'saving' : undefined}
                    >
                      {isSaving ? 'Saving…' : 'Save changes'}
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
              </div>
            )}
            {actions}
          </div>
        </div>
      )}
      {scrollable ? (
        <div className='flex-1 overflow-y-auto px-4 lg:px-6 pb-4 lg:pb-6 pt-4'>
          {children}
        </div>
      ) : (
        children
      )}
      {changeCount > 0 && onSave && (
        <div className='lg:hidden sticky bottom-0 z-10 border-t border-gray-200 dark:border-gray-700 bg-primary px-4 py-3'>
          <div className='flex items-center justify-between gap-3'>
            <span className='text-sm font-medium text-secondary'>
              {changeCount} unsaved change{changeCount !== 1 ? 's' : ''}
            </span>
            <Button
              type='button'
              color='primary'
              size='sm'
              onClick={onSave}
              isDisabled={isSaving}
              isLoading={isSaving}
              // Mirrors the desktop save button above — see comment there
              // for why `data-state` is preferred over locator-by-label.
              data-state={isSaving ? 'saving' : undefined}
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
